import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

/**
 * Chat GFM tables.
 *
 * Preview and TipTap use the same 14rem column cap
 * (document-reading-prose.css). Extra columns scroll inside the message
 * instead of running off as one long line.
 */
const CELL_INNER =
  'max-w-[14rem] whitespace-normal break-words [overflow-wrap:anywhere] [word-break:break-word] [&_code]:whitespace-pre-wrap [&_code]:break-words [&_a]:break-words'

type WithNode = { node?: unknown }

function omitNode<T extends WithNode>(props: T): Omit<T, 'node'> {
  const { node: _node, ...rest } = props
  return rest
}

export function ChatMarkdownTable({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLTableElement> & WithNode) {
  const rest = omitNode(props)
  return (
    <div className="not-prose my-2 w-full min-w-0 max-w-full overflow-x-auto rounded-md border border-border">
      <table
        className={cn('w-max min-w-full border-collapse text-left text-[15px]', className)}
        {...rest}
      >
        {children}
      </table>
    </div>
  )
}

export function ChatMarkdownThead({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement> & WithNode) {
  const rest = omitNode(props)
  return (
    <thead className={cn('border-b border-border bg-muted/50', className)} {...rest}>
      {children}
    </thead>
  )
}

export function ChatMarkdownTbody({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement> & WithNode) {
  const rest = omitNode(props)
  return (
    <tbody className={cn('divide-y divide-border/50', className)} {...rest}>
      {children}
    </tbody>
  )
}

export function ChatMarkdownTr({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLTableRowElement> & WithNode) {
  const rest = omitNode(props)
  return (
    <tr className={cn('transition-colors hover:bg-muted/30', className)} {...rest}>
      {children}
    </tr>
  )
}

export function ChatMarkdownTh({
  children,
  className,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & WithNode) {
  const rest = omitNode(props)
  return (
    <th className={cn('px-3 py-2 align-top font-semibold text-foreground/90', className)} {...rest}>
      <div className={CELL_INNER}>{children}</div>
    </th>
  )
}

export function ChatMarkdownTd({
  children,
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & WithNode) {
  const rest = omitNode(props)
  return (
    <td className={cn('px-3 py-2 align-top text-foreground/80', className)} {...rest}>
      <div className={CELL_INNER}>{children}</div>
    </td>
  )
}

export const chatMarkdownTableComponents = {
  table: ChatMarkdownTable,
  thead: ChatMarkdownThead,
  tbody: ChatMarkdownTbody,
  tr: ChatMarkdownTr,
  th: ChatMarkdownTh,
  td: ChatMarkdownTd,
}
